import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { editorStore } from "../../store/editorStore";
import { FillPattern } from "../../types/floorplannerTypes";
import {
  displayLengthToMm,
  lengthUnitLabel,
  mmToDisplayLength,
} from "../../utils/conversions";
import { getPatternPreviewDataURL } from "../../utils/patternTextures";
import {
  getRoomShapeDefinition,
  RoomShapeId,
  RoomShapeParams,
  wallLengthsFromVertices,
} from "./roomShapeTemplates";

interface RoomDimensionEditorProps {
  shapeId: RoomShapeId;
  params: RoomShapeParams;
  onChangeParams: (next: RoomShapeParams) => void;
  /** Floor fill color hex for live preview. */
  floorColorHex?: string;
  /** Optional floor pattern shown as a multiply overlay. */
  floorPattern?: FillPattern;
  /** Wall stroke color hex for live preview. */
  wallColorHex?: string;
}

interface LabelScreenPos {
  left: number;
  top: number;
  /** Degrees; keeps the label readable along the wall. */
  rotationDeg: number;
  start: { left: number; top: number };
  end: { left: number; top: number };
}

/**
 * Interactive 2D plan: click a wall to type its length.
 * Params stay in mm; display follows editorStore.displayLengthUnit (mm | in).
 */
export const RoomDimensionEditor: React.FC<RoomDimensionEditorProps> = observer(({
  shapeId,
  params,
  onChangeParams,
  floorColorHex = "#F5F0E6",
  floorPattern,
  wallColorHex = "#111111",
}) => {
  const { t } = useTranslation();
  const unit = editorStore.displayLengthUnit;
  const unitLabel = lengthUnitLabel(unit);
  const definition = getRoomShapeDefinition(shapeId);
  const vertices = useMemo(
    () => definition.getVertices(params),
    [definition, params],
  );
  const lengths = useMemo(
    () => wallLengthsFromVertices(vertices),
    [vertices],
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedWall, setSelectedWall] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [labelPos, setLabelPos] = useState<LabelScreenPos | null>(null);

  const formatDisplayMm = (mm: number) => {
    const display = mmToDisplayLength(mm, unit);
    return unit === "in" ? String(display) : String(Math.round(display));
  };

  const xs = vertices.map((v) => v.x);
  const ys = vertices.map((v) => v.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  // Keep the plan zoomed out so walls/labels have breathing room in the modal.
  const pad = Math.max(w, h) * 0.35;

  const toSvg = (x: number, y: number) => ({
    x: x - minX,
    y: maxY - y,
  });

  const midPoint = (i: number) => {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };

  const outwardOffset = (i: number, dist: number) => {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len;
    const ny = -dx / len;
    return { x: nx * dist, y: ny * dist };
  };

  const selectWall = (wallIndex: number) => {
    setSelectedWall(wallIndex);
    setDraft(formatDisplayMm(lengths[wallIndex]));
    setValidationMessage(null);
  };

  const commitWallLength = (wallIndex: number, raw: string) => {
    const parsed = Number.parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setValidationMessage(t("roomPicker.invalidLength"));
      return;
    }
    const lengthMm = displayLengthToMm(parsed, unit);
    const result = definition.applyWallLength(params, wallIndex, lengthMm);
    onChangeParams(result.params);
    if (result.wasClamped) {
      setValidationMessage(
        t("roomPicker.minLengthApplied", {
          min: formatDisplayMm(result.minLengthMm),
          unit: unitLabel,
          applied: formatDisplayMm(result.appliedLengthMm),
        }),
      );
      setDraft(formatDisplayMm(result.appliedLengthMm));
    } else {
      setValidationMessage(null);
      setSelectedWall(null);
      setLabelPos(null);
    }
  };

  // Refresh the open draft when the user toggles mm/in.
  useEffect(() => {
    if (selectedWall === null) return;
    setDraft(formatDisplayMm(lengths[selectedWall]));
    setValidationMessage(null);
    // lengths + unit drive the displayed draft; selectedWall gates the update.
  }, [unit]);

  // Place the label + blue markers in screen space along the selected wall.
  useLayoutEffect(() => {
    if (selectedWall === null) {
      setLabelPos(null);
      return;
    }

    const updatePosition = () => {
      const wrap = wrapRef.current;
      const svg = svgRef.current;
      if (!wrap || !svg) return;

      const a = vertices[selectedWall];
      const b = vertices[(selectedWall + 1) % vertices.length];
      const mid = midPoint(selectedWall);
      const ctm = svg.getScreenCTM();
      if (!ctm) return;

      const wrapRect = wrap.getBoundingClientRect();
      const toScreen = (x: number, y: number) => {
        const svgPt = toSvg(x, y);
        const point = svg.createSVGPoint();
        point.x = svgPt.x;
        point.y = svgPt.y;
        const screen = point.matrixTransform(ctm);
        return {
          left: screen.x - wrapRect.left,
          top: screen.y - wrapRect.top,
        };
      };

      const start = toScreen(a.x, a.y);
      const end = toScreen(b.x, b.y);
      const center = toScreen(mid.x, mid.y);
      const dx = end.left - start.left;
      const dy = end.top - start.top;
      let rotationDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
      if (rotationDeg > 90 || rotationDeg < -90) {
        rotationDeg += 180;
      }

      setLabelPos({
        left: center.left,
        top: center.top,
        rotationDeg,
        start,
        end,
      });
    };

    const raf = window.requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePosition);
    };
  }, [selectedWall, params, shapeId, vertices, w, h]);

  // Select the length value once the inline editor is on screen.
  useEffect(() => {
    if (selectedWall === null || !labelPos) return;
    const timeout = window.setTimeout(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      input.select();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [selectedWall, labelPos]);

  const polygonPoints = vertices
    .map((v) => {
      const p = toSvg(v.x, v.y);
      return `${p.x},${p.y}`;
    })
    .join(" ");

  const patternId = `room-floor-pattern-${shapeId}`;
  const patternPreviewUrl = floorPattern
    ? getPatternPreviewDataURL(floorPattern)
    : null;
  // Tile size in plan mm so the pattern reads clearly at typical room sizes.
  const patternTileMm = Math.max(w, h) * 0.18;
  const wallStroke = Math.max(w, h) * 0.028;

  return (
    <div
      ref={wrapRef}
      className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#ECECEC]"
      onClick={() => {
        if (selectedWall !== null) {
          setSelectedWall(null);
          setValidationMessage(null);
          setLabelPos(null);
        }
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}`}
        className="h-full max-h-full w-full max-w-full"
      >
        {patternPreviewUrl && (
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width={patternTileMm}
              height={patternTileMm}
            >
              <image
                href={patternPreviewUrl}
                x={0}
                y={0}
                width={patternTileMm}
                height={patternTileMm}
                preserveAspectRatio="none"
              />
            </pattern>
          </defs>
        )}
        <polygon points={polygonPoints} fill={floorColorHex} />
        {patternPreviewUrl && (
          <polygon
            points={polygonPoints}
            fill={`url(#${patternId})`}
            style={{ mixBlendMode: "multiply" }}
            opacity={0.55}
          />
        )}
        <polygon
          points={polygonPoints}
          fill="none"
          stroke={wallColorHex}
          strokeWidth={wallStroke}
          strokeLinejoin="round"
        />
        {/* Dark outline so light wall colors stay readable on the canvas. */}
        <polygon
          points={polygonPoints}
          fill="none"
          stroke="#111111"
          strokeWidth={Math.max(w, h) * 0.008}
          strokeLinejoin="round"
          opacity={0.35}
        />
        {vertices.map((_, i) => {
          const a = toSvg(vertices[i].x, vertices[i].y);
          const b = toSvg(
            vertices[(i + 1) % vertices.length].x,
            vertices[(i + 1) % vertices.length].y,
          );
          const selected = selectedWall === i;
          return (
            <line
              key={`wall-${i}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={selected ? "#2563eb" : "transparent"}
              strokeWidth={Math.max(w, h) * 0.04}
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                selectWall(i);
              }}
            />
          );
        })}
        {lengths.map((length, i) => {
          const mid = midPoint(i);
          const offset = outwardOffset(i, Math.max(w, h) * 0.06);
          const p = toSvg(mid.x + offset.x, mid.y + offset.y);
          const label = `${formatDisplayMm(length)} ${unitLabel}`;
          if (selectedWall === i) {
            return null;
          }
          return (
            <g key={`label-${i}`}>
              <text
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="cursor-pointer fill-gray-700"
                style={{ fontSize: Math.max(w, h) * 0.045 }}
                onClick={(e) => {
                  e.stopPropagation();
                  selectWall(i);
                }}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>

      {selectedWall !== null && labelPos && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow"
            style={{ left: labelPos.start.left, top: labelPos.start.top }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow"
            style={{ left: labelPos.end.left, top: labelPos.end.top }}
          />
          <div
            className="absolute z-10"
            style={{
              left: labelPos.left,
              top: labelPos.top,
              transform: `translate(-50%, -50%) rotate(${labelPos.rotationDeg}deg)`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <label className="flex items-center gap-0.5 rounded border border-blue-400 bg-white px-1.5 py-0.5 shadow-sm">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                autoFocus
                value={draft}
                aria-label={t("roomPicker.wallLengthInput")}
                onChange={(e) => {
                  setDraft(e.target.value.replace(/[^\d.]/g, ""));
                  setValidationMessage(null);
                }}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() => {
                  if (selectedWall !== null) {
                    commitWallLength(selectedWall, draft);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitWallLength(selectedWall, draft);
                  } else if (e.key === "Escape") {
                    setSelectedWall(null);
                    setValidationMessage(null);
                    setLabelPos(null);
                  }
                }}
                onMouseDown={(e) => {
                  // Keep the initial click from clearing a programmatic selection.
                  if (document.activeElement !== e.currentTarget) {
                    e.preventDefault();
                    e.currentTarget.focus();
                    e.currentTarget.select();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.select();
                }}
                className="w-14 select-text border-0 bg-transparent p-0 text-center text-xs font-medium text-black focus:outline-none focus:ring-0"
              />
              <span className="text-[10px] font-medium text-black">
                {unitLabel}
              </span>
            </label>
            {validationMessage && (
              <p
                className="pointer-events-none absolute left-1/2 top-full mt-1 w-max max-w-[220px] -translate-x-1/2 rounded bg-white/95 px-2 py-1 text-[10px] text-amber-700 shadow"
                style={{
                  transform: `translate(-50%, 0) rotate(${-labelPos.rotationDeg}deg)`,
                }}
                role="status"
              >
                {validationMessage}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
});
